import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Session } from "@cs2/contracts/transport";
import { readProgress, writeProgress } from "./onboarding-cookie.js";
import {
  pendingIn,
  stepsFor,
  summarize,
  type OnboardingProgress,
  type StepStatus,
} from "./onboarding-progress.js";
import type { OnboardingChapter } from "./onboarding-steps.js";

type View = {
  chapter: OnboardingChapter;
  index: number;
  manual: boolean;
  /** Automatisch geöffnet: nur die beim Öffnen noch offenen Schritte. */
  only?: readonly string[];
};

/**
 * Steuert die Tour für die aktive Seite: öffnet das Kapitel einmal pro Mount
 * automatisch, solange darin noch Schritte ohne Status liegen, und hält den
 * Fortschritt im Cookie des angemeldeten Accounts. Automatisch zeigt sie nur
 * diese offenen Schritte — wer die Tour kennt, sieht nach einem Release nur
 * die neuen. Über „Tour & Hilfe“ ist das ganze Kapitel erreichbar.
 */
export function useOnboarding(
  session: Session | null | undefined,
  active: OnboardingChapter,
  enabled: boolean,
) {
  const user = session?.authenticated ? session.user : null;
  const userId = user?.id ?? null;
  const role = user?.role ?? null;
  const [progress, setProgress] = useState<OnboardingProgress>(() =>
    userId ? readProgress(userId) : {},
  );
  const [view, setView] = useState<View | null>(null);
  const autoOpened = useRef(false);

  useEffect(() => {
    if (!enabled || !userId || !role || autoOpened.current) return;
    autoOpened.current = true;
    const stored = readProgress(userId);
    setProgress(stored);
    const pending = pendingIn(stepsFor(role, active), stored);
    if (pending.length > 0)
      setView({
        chapter: active,
        index: 0,
        manual: false,
        only: pending.map((step) => step.id),
      });
  }, [enabled, userId, role, active]);

  const steps = useMemo(() => {
    if (!role || !view) return [];
    const chapterSteps = stepsFor(role, view.chapter);
    const only = view.only;
    return only
      ? chapterSteps.filter((step) => only.includes(step.id))
      : chapterSteps;
  }, [role, view]);
  const current = view ? steps[view.index] : undefined;

  const persist = useCallback(
    (next: OnboardingProgress) => {
      if (!userId) return;
      writeProgress(userId, next);
      setProgress(next);
    },
    [userId],
  );

  function mark(status: StepStatus) {
    if (!current) return progress;
    // Ein bereits erledigter Schritt wird durch Überspringen nicht abgewertet.
    if (status === "skipped" && progress[current.id] === "done")
      return progress;
    const next = { ...progress, [current.id]: status };
    persist(next);
    return next;
  }

  function advance() {
    setView((value) =>
      value && value.index < steps.length - 1
        ? { ...value, index: value.index + 1 }
        : null,
    );
  }

  function firstPendingIndex(chapter: OnboardingChapter) {
    if (!role) return 0;
    const chapterSteps = stepsFor(role, chapter);
    const first = pendingIn(chapterSteps, progress)[0];
    return first ? chapterSteps.indexOf(first) : 0;
  }

  return {
    available: Boolean(enabled && userId && role),
    open: view !== null && current !== undefined,
    chapter: view?.chapter ?? active,
    manual: view?.manual ?? false,
    index: view?.index ?? 0,
    steps,
    current,
    progress,
    summary: summarize(steps, progress),
    chapterSummary: (chapter: OnboardingChapter) =>
      role
        ? summarize(stepsFor(role, chapter), progress)
        : summarize([], progress),
    openTour: (chapter: OnboardingChapter = active) => {
      if (!userId || !role) return;
      const stored = readProgress(userId);
      setProgress(stored);
      const chapterSteps = stepsFor(role, chapter);
      const first = pendingIn(chapterSteps, stored)[0];
      setView({
        chapter,
        index: first ? chapterSteps.indexOf(first) : 0,
        manual: true,
      });
    },
    selectChapter: (chapter: OnboardingChapter) =>
      setView((value) =>
        value
          ? { ...value, chapter, index: firstPendingIndex(chapter) }
          : value,
      ),
    close: () => setView(null),
    next: () => {
      mark("done");
      advance();
    },
    skipStep: () => {
      mark("skipped");
      advance();
    },
    skipRest: () => {
      const next = { ...progress };
      for (const step of pendingIn(steps, progress)) next[step.id] = "skipped";
      persist(next);
      setView(null);
    },
    back: () =>
      setView((value) =>
        value && value.index > 0 ? { ...value, index: value.index - 1 } : value,
      ),
    goTo: (index: number) =>
      setView((value) => (value ? { ...value, index } : value)),
  };
}

export type OnboardingController = ReturnType<typeof useOnboarding>;
